import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Divider,
  Stack,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { convertToPlaces } from "../../helpers/helpers";

const QuantityChangeConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  changes = [],
  totals = { originalGrandTotal: 0, newGrandTotal: 0, difference: 0 },
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Confirm Quantity Changes</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The following items have quantity changes that will affect pricing.
          Please review and confirm these changes before proceeding:
        </DialogContentText>
        
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell>Item</TableCell>
                <TableCell align="center">Original Qty</TableCell>
                <TableCell align="center">New Qty</TableCell>
                <TableCell align="right">Original Unit Price</TableCell>
                <TableCell align="right">New Unit Price</TableCell>
                <TableCell align="right">Original Total</TableCell>
                <TableCell align="right">New Total</TableCell>
                <TableCell align="right">Difference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map((change, index) => {
                const priceDiff = change.newTotalPrice - change.originalTotalPrice;
                return (
                  <TableRow key={index}>
                    <TableCell>{change.itemName}</TableCell>
                    <TableCell align="center">
                      <Typography 
                        sx={{ textDecoration: "line-through", color: "text.secondary" }}
                      >
                        {change.originalQty}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold" color="primary">
                        {change.newQty}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {convertToPlaces(change.originalUnitPrice)}
                    </TableCell>
                    <TableCell align="right">
                      {convertToPlaces(change.newUnitPrice)}
                    </TableCell>
                    <TableCell align="right">
                      {convertToPlaces(change.originalTotalPrice)}
                    </TableCell>
                    <TableCell align="right">
                      {convertToPlaces(change.newTotalPrice)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: priceDiff > 0 ? "error.main" : "success.main",
                        fontWeight: "bold",
                      }}
                    >
                      {priceDiff > 0 ? "+" : ""}
                      {convertToPlaces(priceDiff)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Section */}
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: "grey.50" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Order Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Original Total:</Typography>
              <Typography>{convertToPlaces(totals.originalGrandTotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>New Total:</Typography>
              <Typography fontWeight="bold">{convertToPlaces(totals.newGrandTotal)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight="bold">
                {totals.difference >= 0 ? "Additional Amount:" : "Savings:"}
              </Typography>
              <Typography 
                fontWeight="bold"
                sx={{ 
                  color: totals.difference > 0 ? "error.main" : "success.main" 
                }}
              >
                {totals.difference > 0 ? "+" : ""}
                {convertToPlaces(Math.abs(totals.difference))}
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          <strong>Note:</strong> By confirming, you acknowledge the quantity and price changes. 
          The original order data will be saved for reference. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} color="inherit" variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="primary"
          autoFocus
        >
          Confirm Changes & Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuantityChangeConfirmDialog;
